import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { buttonVariants } from '@/components/ui/Button';
import { useAuth, useOrganization } from '@/features/auth/hooks';

/*
 * TrialBanner (Sessio 8b).
 *
 * Renderoidaan AppLayout:n paaalueen ylaosaan (EmailVerifyBannerin alla) kun
 * kirjautunut kayttaja on Luviamo-trial:ssa (`plan === 'trial' &&
 * subscription_status === null`). Stripe-tilauksessa olevat trial:t (kuten Solo-
 * trial Stripe-kuukauden alussa) eivat naytetaan -- he ovat jo tilanneet planin.
 *
 * Sisalto:
 *   X paivaa trial-aikaa jaljella    [Paivita plan ->]
 *
 * Reuna varittyy oranssiksi kun <= 3 pv jaljella (jolloin kayttajaa kannattaa
 * muistuttaa). Trial-loppu lasketaan organization.trial_ends_at:sta (SQLite-
 * formaatti `YYYY-MM-DD HH:MM:SS`).
 */

const URGENT_THRESHOLD_DAYS = 3;
const DAY_MS = 86_400_000;

function parseSqliteDatetimeUtc(value: string): Date {
  // Hyvaksyy seka SQLite-muodon `YYYY-MM-DD HH:MM:SS` (UTC) etta ISO-8601:n --
  // OAuth-rekisterointi kirjoitti trial_ends_at:n ISO-muodossa, jolloin pelkka
  // `+ 'Z'` tuotti '...ZZ' -> Invalid Date -> "NaN paivaa" (Joanna 24.8.2026).
  // Sama kuvio kuin SubscriptionStatusCard.formatDate (LESSONS 17.5.2026).
  const isoLike = value.includes('T') ? value : value.replace(' ', 'T') + 'Z';
  return new Date(isoLike);
}

export function TrialBanner() {
  const { t } = useTranslation();
  const { status } = useAuth();
  const organization = useOrganization();

  if (status !== 'authenticated' || !organization) return null;
  if (organization.plan !== 'trial' || organization.subscription_status !== null) {
    return null;
  }

  const { trial_ends_at } = organization;
  let label: string;
  let urgent = false;

  if (!trial_ends_at) {
    // Vanha kayttaja ennen Sessio 8b backfill:ia -- nayta yleinen upgrade-CTA.
    label = t('billing.trial.upgrade_cta');
  } else {
    const endTime = parseSqliteDatetimeUtc(trial_ends_at).getTime();
    const daysLeft = Math.max(0, Math.ceil((endTime - Date.now()) / DAY_MS));
    urgent = daysLeft <= URGENT_THRESHOLD_DAYS;

    if (daysLeft === 0) {
      label = t('billing.trial.expired');
    } else if (daysLeft === 1) {
      label = t('billing.trial.tomorrow');
    } else {
      label = t('billing.trial.days_left', { days: daysLeft });
    }
  }

  const colorClasses = urgent
    ? 'border-oranssi bg-oranssi/10 text-teksti'
    : 'border-kulta bg-kulta/15 text-teksti';

  return (
    <div
      role="region"
      aria-label={t('billing.trial.regionLabel')}
      className={['border-b px-6 py-3 text-sm lg:px-10', colorClasses].join(' ')}
    >
      <div className="mx-auto flex max-w-[var(--content-max)] flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <strong className="font-semibold">{label}</strong>
        <Link
          to="/billing"
          className={buttonVariants({ variant: 'secondary', size: 'sm' })}
        >
          {t('billing.trial.upgrade_cta')}
        </Link>
      </div>
    </div>
  );
}
